import type { Api, PublicRace } from "../api";
import type {
  MapboxChoice,
  MapboxSearch,
  MapboxSuggestion,
} from "../mapbox";
import {
  MapboxSearchError,
  newMapboxSessionToken,
} from "../mapbox";
import type { MyBallotSnapshot } from "../my-ballot-state";
import { h, uid } from "../dom";

type FinderState = "idle" | "suggesting" | "resolving" | "matched" | "error";

interface FinderOptions {
  mapbox: MapboxSearch | null;
  api: Api;
  cycle: string;
  ballot: string;
  snapshot: MyBallotSnapshot | null;
  signal: AbortSignal;
  debounceMs?: number;
  onMatched(result: { choice: MapboxChoice; raceSlugs: string[] }): void;
  onReturn(): void;
}

type Option =
  | { kind: "suggestion"; value: MapboxSuggestion }
  | { kind: "choice"; value: MapboxChoice };

export function filterRacesForMyBallot(
  races: PublicRace[],
  raceSlugs: string[]
): PublicRace[] {
  const matched = new Set(raceSlugs);
  return races.filter((race) => matched.has(race.slug));
}

function attribution() {
  return h(
    "p",
    { class: "bsc-ballot-finder__attribution" },
    "Address search powered by ",
    h(
      "a",
      {
        href: "https://www.mapbox.com/about/maps/",
        target: "_blank",
        rel: "noopener noreferrer",
      },
      "Mapbox"
    ),
    "."
  );
}

function matchedFinder(snapshot: MyBallotSnapshot, onReturn: () => void) {
  const count = new Set(snapshot.raceSlugs).size;
  const headingText = `Your ballot includes ${count} ${count === 1 ? "Race" : "Races"}`;
  const root = h("section", {
    class: "bsc-ballot-finder",
    "data-my-ballot-state": "matched",
    "aria-label": "My ballot",
  });
  const button = h(
    "button",
    { type: "button", class: "bsc-my-ballot__return" },
    "Return to all candidates"
  );
  button.addEventListener("click", onReturn);
  root.append(
    h(
      "div",
      { class: "bsc-my-ballot" },
      h(
        "h2",
        {
          class: "bsc-my-ballot__heading",
          tabindex: "-1",
          "data-bsc-view-focus": true,
          "data-bsc-view-announcement": `${count} ${count === 1 ? "Race matches" : "Races match"} your address.`,
        },
        headingText
      ),
      h(
        "p",
        { class: "bsc-my-ballot__summary" },
        "Showing every candidate in the Races that apply at this address."
      ),
      h(
        "div",
        { class: "bsc-my-ballot__context" },
        h("span", null, "Address"),
        h("strong", { class: "bsc-my-ballot__address" }, snapshot.displayLabel)
      ),
      button
    ),
    attribution()
  );
  return root;
}

export function renderBallotFinder(options: FinderOptions): HTMLElement {
  if (options.snapshot) {
    return matchedFinder(options.snapshot, options.onReturn);
  }

  const root = h("section", {
    class: "bsc-ballot-finder",
    "data-my-ballot-state": "idle",
    "aria-label": "Find my ballot",
  });
  const inputId = uid("ballot-address");
  const listId = uid("ballot-suggestions");
  const errorId = uid("ballot-error");
  const label = h(
    "label",
    { class: "bsc-ballot-finder__label", for: inputId },
    "Home address"
  );
  const input = h("input", {
    id: inputId,
    class: "bsc-ballot-finder__input",
    type: "text",
    autocomplete: "street-address",
    role: "combobox",
    "aria-autocomplete": "list",
    "aria-expanded": "false",
    "aria-controls": listId,
    "aria-describedby": errorId,
    placeholder: "Enter your address",
  });
  const submit = h(
    "button",
    { type: "submit", class: "bsc-ballot-finder__submit" },
    "Find my ballot",
    h("span", { class: "bsc-ballot-finder__submit-arrow", "aria-hidden": "true" }, "→")
  );
  const list = h("ul", {
    id: listId,
    class: "bsc-ballot-finder__suggestions",
    role: "listbox",
    hidden: true,
  });
  const error = h("div", {
    id: errorId,
    class: "bsc-ballot-finder__error",
    role: "alert",
    "aria-live": "assertive",
  });
  // The list sits in the address column, so it ends at the button's left edge.
  const form = h("form", { class: "bsc-ballot-finder__form", novalidate: true }, label, input, submit, list);
  root.append(form, error, attribution());

  const debounceMs = options.debounceMs ?? 150;
  let sessionToken = newMapboxSessionToken();
  let sequence = 0;
  let active = -1;
  let currentOptions: Option[] = [];
  let suggestTimer: ReturnType<typeof setTimeout> | null = null;
  let requestController: AbortController | null = null;
  let retryAction: (() => void) | null = null;
  let countdownTimer: ReturnType<typeof setInterval> | null = null;
  let resolving = false;

  const setState = (state: FinderState) => {
    root.dataset.myBallotState = state;
  };

  function clearTimer() {
    if (suggestTimer) clearTimeout(suggestTimer);
    suggestTimer = null;
    if (countdownTimer) clearInterval(countdownTimer);
    countdownTimer = null;
  }

  function cancel() {
    sequence += 1;
    requestController?.abort();
    requestController = null;
    clearTimer();
    resolving = false;
    submit.disabled = false;
  }

  function closeOptions() {
    currentOptions = [];
    active = -1;
    list.replaceChildren();
    list.hidden = true;
    input.setAttribute("aria-expanded", "false");
    input.removeAttribute("aria-activedescendant");
  }

  function markActive() {
    list.querySelectorAll<HTMLElement>('[role="option"]').forEach((node, index) => {
      node.setAttribute("aria-selected", String(index === active));
    });
    const node = list.querySelectorAll<HTMLElement>('[role="option"]')[active];
    if (node) input.setAttribute("aria-activedescendant", node.id);
    else input.removeAttribute("aria-activedescendant");
  }

  function showOptions(values: Option[]) {
    currentOptions = values;
    active = -1;
    list.replaceChildren(
      ...values.map((option, index) => {
        const node = h(
          "li",
          {
            id: `${listId}-${index}`,
            class: "bsc-ballot-finder__suggestion",
            role: "option",
            "aria-selected": "false",
            tabindex: "-1",
          },
          option.value.label
        );
        node.addEventListener("mousedown", (event) => event.preventDefault());
        node.addEventListener("click", () => void choose(index));
        return node;
      })
    );
    list.hidden = values.length === 0;
    input.setAttribute("aria-expanded", String(values.length > 0));
  }

  function clearError() {
    error.replaceChildren();
    retryAction = null;
  }

  function showError(message: string, retry?: () => void) {
    resolving = false;
    submit.disabled = false;
    closeOptions();
    setState("error");
    retryAction = retry ?? null;
    const retryButton = retry
      ? h("button", { type: "button", class: "bsc-ballot-finder__retry" }, "Try again")
      : null;
    retryButton?.addEventListener("click", () => retryAction?.());
    error.replaceChildren(h("p", null, message), ...(retryButton ? [retryButton] : []));
  }

  function showRateLimit(seconds: number, retry: () => void) {
    let remaining = Math.max(1, Math.ceil(seconds));
    showError(`Too many requests. Try again in ${remaining} seconds.`, retry);
    const button = error.querySelector<HTMLButtonElement>(".bsc-ballot-finder__retry")!;
    button.disabled = true;
    countdownTimer = setInterval(() => {
      remaining -= 1;
      const message = error.querySelector("p");
      if (remaining > 0) {
        if (message) message.textContent = `Too many requests. Try again in ${remaining} seconds.`;
        return;
      }
      if (countdownTimer) clearInterval(countdownTimer);
      countdownTimer = null;
      if (message) message.textContent = "You can try the ballot lookup again.";
      button.disabled = false;
    }, 1000);
  }

  async function resolveChoice(selected: MapboxChoice) {
    if (resolving || options.signal.aborted) return;
    cancel();
    const operation = sequence;
    resolving = true;
    submit.disabled = true;
    clearError();
    closeOptions();
    setState("resolving");
    requestController = new AbortController();
    const abort = () => requestController?.abort();
    options.signal.addEventListener("abort", abort, { once: true });
    const retry = () => void resolveChoice(selected);
    try {
      const result = await options.api.getMyBallot(
        options.cycle,
        options.ballot,
        { latitude: selected.latitude, longitude: selected.longitude },
        requestController.signal
      );
      if (operation !== sequence || options.signal.aborted) return;
      resolving = false;
      submit.disabled = false;
      if (result.ok) {
        if (result.data.outcome === "outside-scope") {
          showError("Only Cook County addresses supported");
          return;
        }
        setState("matched");
        options.onMatched({ choice: selected, raceSlugs: result.data.raceSlugs });
        return;
      }
      if (result.reason === "aborted") return;
      if (result.reason === "rate-limited") {
        showRateLimit(result.retryAfterSeconds ?? 60, retry);
      } else if (result.reason === "map-unavailable" || result.reason === "data-incomplete") {
        showError(
          "A complete ballot cannot yet be generated for this election. All candidates remain available."
        );
      } else if (result.reason === "not-found") {
        showError("This ballot is no longer available. All candidates remain available.");
      } else {
        showError("Ballot lookup is temporarily unavailable. Try again.", retry);
      }
    } finally {
      options.signal.removeEventListener("abort", abort);
    }
  }

  async function choose(index: number) {
    const option = currentOptions[index];
    if (!option || resolving || !options.mapbox) return;
    input.value = option.value.label;
    if (option.kind === "choice") {
      sessionToken = newMapboxSessionToken();
      await resolveChoice(option.value);
      return;
    }
    cancel();
    const operation = sequence;
    resolving = true;
    submit.disabled = true;
    clearError();
    closeOptions();
    setState("resolving");
    requestController = new AbortController();
    const retry = () => {
      currentOptions = [option];
      void choose(0);
    };
    try {
      const results = await options.mapbox.retrieve(
        option.value.id,
        sessionToken,
        requestController.signal
      );
      sessionToken = newMapboxSessionToken();
      if (operation !== sequence || options.signal.aborted) return;
      resolving = false;
      submit.disabled = false;
      if (results.length === 0) {
        showError("We couldn't find that address. Check it and try again.", retry);
        return;
      }
      await resolveChoice(results[0]);
    } catch (cause) {
      if (operation !== sequence || options.signal.aborted) return;
      resolving = false;
      submit.disabled = false;
      if (cause instanceof MapboxSearchError && cause.reason === "aborted") return;
      showError("Address search is temporarily unavailable. Try again.", retry);
    }
  }

  async function forward(query: string) {
    if (resolving) return;
    if (!options.mapbox) {
      showError("Address search is temporarily unavailable. Try again.", () => void forward(query));
      return;
    }
    cancel();
    const operation = sequence;
    resolving = true;
    submit.disabled = true;
    clearError();
    closeOptions();
    setState("resolving");
    requestController = new AbortController();
    const retry = () => void forward(query);
    try {
      const results = await options.mapbox.forward(query, requestController.signal);
      sessionToken = newMapboxSessionToken();
      if (operation !== sequence || options.signal.aborted) return;
      resolving = false;
      submit.disabled = false;
      if (results.length === 0) {
        showError("We couldn't find that address. Check it and try again.", retry);
      } else if (results.length === 1) {
        await resolveChoice(results[0]);
      } else {
        setState("idle");
        showOptions(results.map((value) => ({ kind: "choice" as const, value })));
      }
    } catch (cause) {
      if (operation !== sequence || options.signal.aborted) return;
      resolving = false;
      submit.disabled = false;
      if (cause instanceof MapboxSearchError && cause.reason === "aborted") return;
      showError("Address search is temporarily unavailable. Try again.", retry);
    }
  }

  // Suggestions stay on screen while the next request is in flight and are replaced when
  // it answers, so the list does not blink shut on every keystroke.
  input.addEventListener("input", () => {
    cancel();
    clearError();
    const query = input.value.trim();
    if (query.length < 3 || !options.mapbox) {
      closeOptions();
      setState("idle");
      return;
    }
    const operation = sequence;
    setState("suggesting");
    suggestTimer = setTimeout(async () => {
      requestController = new AbortController();
      try {
        const results = await options.mapbox!.suggest(
          query,
          sessionToken,
          requestController.signal
        );
        if (operation !== sequence || options.signal.aborted) return;
        setState("idle");
        showOptions(results.map((value) => ({ kind: "suggestion" as const, value })));
      } catch (cause) {
        if (operation !== sequence || options.signal.aborted) return;
        if (cause instanceof MapboxSearchError && cause.reason === "aborted") return;
        showError("Address search is temporarily unavailable. Try again.", () => {
          input.dispatchEvent(new Event("input", { bubbles: true }));
        });
      }
    }, debounceMs);
  });

  input.addEventListener("keydown", (event) => {
    if (event.key === "Escape") {
      closeOptions();
      return;
    }
    if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      if (currentOptions.length === 0) return;
      event.preventDefault();
      const direction = event.key === "ArrowDown" ? 1 : -1;
      active = (active + direction + currentOptions.length) % currentOptions.length;
      markActive();
      return;
    }
    if (event.key === "Enter" && active >= 0) {
      event.preventDefault();
      void choose(active);
    }
  });

  form.addEventListener("submit", (event) => {
    event.preventDefault();
    const query = input.value.trim();
    if (!query || resolving) return;
    void forward(query);
  });

  // Leaving the finder closes the list; choosing an option keeps focus inside it.
  root.addEventListener("focusout", (event) => {
    const next = (event as FocusEvent).relatedTarget as Node | null;
    if (!next || !root.contains(next)) closeOptions();
  });

  options.signal.addEventListener("abort", cancel, { once: true });
  return root;
}

/**
 * Marks a sticky element `data-docked` while it is held at its sticky offset, so the
 * stylesheet can lift it off the content scrolling beneath (prototype B's shadow).
 */
export function attachDockState(el: HTMLElement, signal: AbortSignal): void {
  if (signal.aborted || typeof window === "undefined" || !window.getComputedStyle) return;
  let frame = 0;
  const measure = () => {
    frame = 0;
    const style = window.getComputedStyle(el);
    const top = parseFloat(style.top);
    const docked = style.position === "sticky" && Number.isFinite(top)
      && el.getBoundingClientRect().top <= top + 0.5 && window.scrollY > 0;
    if (docked) el.setAttribute("data-docked", "");
    else el.removeAttribute("data-docked");
  };
  let settle: ReturnType<typeof setTimeout> | null = null;
  const schedule = () => { if (!frame) frame = requestAnimationFrame(measure); };
  // A docked element's `top` eases as the site header comes and goes, so a jump made
  // mid-transition can read a stale offset. Measure again once things settle: after the
  // transition (ended or cancelled) and 250ms after the last scroll.
  const scheduleAndSettle = () => {
    schedule();
    if (settle) clearTimeout(settle);
    settle = setTimeout(() => { settle = null; schedule(); }, 250);
  };
  window.addEventListener("scroll", scheduleAndSettle, { passive: true, signal });
  window.addEventListener("resize", scheduleAndSettle, { passive: true, signal });
  el.addEventListener("transitionend", schedule, { signal });
  el.addEventListener("transitioncancel", schedule, { signal });
  signal.addEventListener("abort", () => {
    if (frame) cancelAnimationFrame(frame);
    if (settle) clearTimeout(settle);
  }, { once: true });
  schedule();
}
